// src/components/WhatsAppShare.js
import { forwardRef, useImperativeHandle } from 'react';

const NAME_MAX = 30;

function money(v) {
  return Number(v || 0).toFixed(2);
}

function trimText(text, maxLen) {
  const str = String(text || '');
  return str.length > maxLen ? `${str.substring(0, maxLen - 2)}..` : str;
}

function cleanProductName(name) {
  return String(name || '').split('(')[0].trim();
}

function getCachedProducts() {
  try {
    const raw = localStorage.getItem('mkpos.products');
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getDirectTeluguName(item) {
  return String(
    item?.teluguname ||
      item?.telugu_name ||
      item?.productTeluguName ||
      item?.product_name_tel ||
      item?.product?.teluguname ||
      item?.product?.telugu_name ||
      item?.product?.productTeluguName ||
      item?.product?.product_name_tel ||
      ''
  ).trim();
}

function getTeluguName(item) {
  const directName = getDirectTeluguName(item);
  if (directName) {
    return directName;
  }

  const cachedProducts = getCachedProducts();
  if (!cachedProducts.length) {
    return '';
  }

  const targetIds = new Set(
    [item?.productId, item?._id, item?.id]
      .filter(Boolean)
      .map((value) => String(value))
  );
  const targetCodes = new Set(
    [item?.product_code, item?.product_barcode_id, item?.mkid]
      .filter(Boolean)
      .map((value) => String(value))
  );
  const targetName = cleanProductName(item?.item || item?.name || '');

  const candidateMatches = (candidate = {}, detail = {}) => {
    const candidateIds = [candidate?._id, candidate?.id]
      .filter(Boolean)
      .map((value) => String(value));
    const candidateCodes = [
      candidate?.product_code,
      candidate?.productCode,
      candidate?.product_barcode_id,
      candidate?.productBarcodeId,
      candidate?.mkid,
      detail?.brandId,
    ]
      .filter(Boolean)
      .map((value) => String(value));

    return (
      candidateIds.some((value) => targetIds.has(value)) ||
      candidateCodes.some((value) => targetCodes.has(value)) ||
      (targetName && cleanProductName(candidate?.name || candidate?.productname || candidate?.englishname || '') === targetName)
    );
  };

  for (const product of cachedProducts) {
    if (!product || typeof product !== 'object') continue;

    if (candidateMatches(product)) {
      const productTelugu = getDirectTeluguName(product);
      if (productTelugu) return productTelugu;
    }

    for (const detail of Array.isArray(product.details) ? product.details : []) {
      if (candidateMatches(product, detail)) {
        const detailTelugu = getDirectTeluguName(detail) || getDirectTeluguName(product);
        if (detailTelugu) return detailTelugu;
      }

      for (const financial of Array.isArray(detail?.financials) ? detail.financials : []) {
        if (candidateMatches(product, financial) || candidateMatches(detail, financial)) {
          const financialTelugu =
            getDirectTeluguName(financial) ||
            getDirectTeluguName(detail) ||
            getDirectTeluguName(product);
          if (financialTelugu) return financialTelugu;
        }
      }
    }
  }

  return '';
}

function formatDateTime(dt) {
  const d = new Date(dt || Date.now());
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

function getItems(order) {
  return order?.orderItems || order?.cartItems || order?.items || [];
}

function firstNumber(...values) {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function getShopHeaderLines() {
  return [
    'Ph No: 81217 74325',
    'GST No: 37AAUCM3501B1ZN',
    'FSSAI No: 10124999000130',
    'Landline: 08856-297898',
  ];
}

function getPaymentLabel(order) {
  const paymentBreakdown = Array.isArray(order?.paymentBreakdown)
    ? order.paymentBreakdown.filter(
        (payment) => payment?.channel && Number(payment.amount || 0) > 0
      )
    : [];

  if (paymentBreakdown.length === 1) {
    return paymentBreakdown[0].channel;
  }

  if (Number(order?.cashGiven || 0) > 0 && paymentBreakdown.length === 0) {
    return 'Cash';
  }

  if (paymentBreakdown.length > 1) {
    return 'Multi';
  }

  if (order?.paymentMethod) {
    return order.paymentMethod;
  }

  return '';
}

function getCashTender(order) {
  const cashGiven = Number(order?.cashGiven || 0);
  if (cashGiven > 0) {
    return cashGiven;
  }

  const paymentBreakdown = Array.isArray(order?.paymentBreakdown)
    ? order.paymentBreakdown.filter(
        (payment) => payment?.channel && Number(payment.amount || 0) > 0
      )
    : [];

  if (paymentBreakdown.length === 1 && String(paymentBreakdown[0].channel || '').toLowerCase() === 'cash') {
    return Number(paymentBreakdown[0].amount || 0);
  }

  return 0;
}

function getPaymentFooterLines(order, paymentLabel) {
  const total = Number(
    order?.total ?? order?.totalPrice ?? order?.grandTotal ?? 0
  );
  const resolvedCashGiven = getCashTender(order);
  const calculatedChange = Math.max(resolvedCashGiven - total, 0);
  const resolvedChange =
    Number.isFinite(Number(order?.change)) && Number(order?.change) > 0
      ? Number(order.change)
      : calculatedChange;
  const lines = [];

  if (paymentLabel === 'Cash' || resolvedCashGiven > 0) {
    lines.push(`Paid Cash: ₹${money(resolvedCashGiven)}`);
    lines.push(`Change : ₹${money(resolvedChange)}`);
    return lines;
  }

  return lines;
}

function buildOrderText(order) {
  const shopName = process.env.REACT_APP_SHOP_NAME || 'MANAKIRANA';

  const items = getItems(order);

  const totalQty =
    order?.totalQty ??
    items.reduce((sum, item) => sum + Number(item?.qty || 0), 0);

  const total =
    order?.total ??
    order?.totalPrice ??
    order?.grandTotal ??
    items.reduce((sum, item) => sum + Number(item?.subtotal || 0), 0);


  const lines = [];

  lines.push(`*${shopName}*`);
  lines.push(...getShopHeaderLines());
  lines.push('');
  lines.push(`Order: ${order?.order_number || order?._id || order?.id || 'NA'}`);
  lines.push(`Date : ${formatDateTime(order?.datetime || order?.createdAt)}`);
  lines.push('------------------------------');

  let totalMrp = 0;
  let totalDiscount = 0;

  items.forEach((item, index) => {
    const name = trimText(cleanProductName(item?.item || ''), NAME_MAX);
    const teluguName = getTeluguName(item);
    const displayName = teluguName ? `${name} (${teluguName})` : name;

    const saleQty = Number(item?.qty || 1); // qty
    const packQty = item?.quantity || '';   // quantity
    const units = item?.units || '';
    const mrpUnit = firstNumber(
      item?.mrp,
      item?.MRP,
      item?.originalPrice,
      item?.price,
      item?.unit_mrp,
      item?.unitMrp,
      item?.dprice,
      item?.pricePerQty
    );
    const discountUnit = firstNumber(
      item?.discountPrice,
      item?.finalPrice,
      item?.dprice,
      item?.pricePerQty,
      item?.price,
      item?.mrp,
      item?.MRP
    );
    const mrpAmount = saleQty * mrpUnit;
    const discountAmount = Number(item?.subtotal || saleQty * discountUnit);

    totalMrp += mrpAmount;
    totalDiscount += Math.max(mrpAmount - discountAmount, 0);

    if (!name) return;

    const packText = packQty && units ? `${packQty}${units}` : '';

    lines.push(
      `${index + 1}. ${displayName} ${packText} x${saleQty}`
    );
    lines.push(
      `${saleQty} x ₹${money(mrpUnit)} = ₹${money(mrpAmount)} | Discount Price: ₹${money(discountAmount)}`
    );
    lines.push('');
  });

  lines.push('------------------------------');
  lines.push(`*Qty:* ${money(totalQty)}`);
  lines.push(`*Total MRP:* ₹${money(totalMrp)}`);
  lines.push(`*Total Discount:* ₹${money(totalDiscount)}`);
  lines.push(`*TOTAL:* ₹${money(total)}`);
  lines.push('------------------------------');

  const paymentLabel = getPaymentLabel(order);
  if (paymentLabel) lines.push(`Pay: ${paymentLabel}`);
  lines.push(...getPaymentFooterLines(order, paymentLabel));

  lines.push('');
  lines.push('Thank you! Visit again');

  return lines.join('\n');
}

function toPhone10(p) {
  return String(p || '').replace(/\D/g, '').slice(-10);
}

function openWhatsapp(phone10, text) {
  window.open(`https://wa.me/91${phone10}?text=${encodeURIComponent(text)}`, '_blank');
}

const WhatsAppShare = forwardRef((_, ref) => {
  useImperativeHandle(ref, () => ({
    sendText(order, phone) {
      const p = toPhone10(phone);
      if (!/^\d{10}$/.test(p)) return alert('Invalid phone number');
      openWhatsapp(p, buildOrderText(order));
    },

    sendImage(order, phone) {
      const p = toPhone10(phone);
      if (!/^\d{10}$/.test(p)) return alert('Invalid phone number');
      openWhatsapp(p, buildOrderText(order));
    },

    getInvoiceText(order) {
      return buildOrderText(order);
    },
  }));

  return null;
});

export default WhatsAppShare;
export { buildOrderText };